import { Form, Input, Alert } from 'antd';
import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import ReactSelectCreatable from 'react-select/creatable';
import ReactSelect from 'react-select';
import moment from 'moment';

import { useSite } from '../../../util/sites';
import { useUser } from '../../../util/user';
import GYAxios from '../../../util/GYAxios';
import Noti from '../../../util/noti';
import { enumRoles } from '../../../util/KeycloakAPIUtil';
import presetEventEmitter, { presetEventNames } from '../../../util/presetEventEmitter';
import PresetSelectOption from './PresetSelectOption';

import styles from './DataDownloadPreset.module.css';
import { useWatchStateChange } from '../../../util/watchStateChange';
import PresetEventEmitter from '../../../util/presetEventEmitter';
import { PresetRoleOptionMultiValueLabel } from './PresetRoleOptionMultiValueLabel';
import { delay } from '../../../util/promise';

export default function DataDownloadPreset({ selectedSites, selectedPreset, selectedSelectBy, selectedContracts, selectedCountry, isAllSelectSelected, onPresetChange, dataType, defaultDateRange, dateRangePickerRanges, currentFieldSelection, resetCurrentFieldSelection, isEditPreset, editingPreset, isCreatePreset, setIsCreatePreset, setEditingPreset, duplicatePresetToFieldSelection, isITStaff }) {
	const isFirstLoadPresets = useRef(true);
	const [presetLoaded, setPresetsLoaded] = useState(false);
	const [allPresets, setAllPresets] = useState(null);
	const currentPreset = editingPreset || selectedPreset || null;
	const allSites = useSite()?.allSites_Select.map(client => client.options.map(option => option.value)).flat(1);
	const user = useUser();

	const [isUserRolesInvalid, setIsUserRolesInvalid] = useState(false);

	// select style
	const selectStyle = useMemo(() => ({
		container: provided  => ({
			...provided,
			width: '300px',
			fontSize: '1.3rem'
		}),
		valueContainer: provided => ({
			...provided,
			overflowY: 'auto',
			height: 'auto',
			maxHeight: '80px',
		}),
		placeholder: provided => ({
			...provided,
			fontSize: '1.2rem'
		})
	}), []);

	// preset options
	const options = useMemo(() => allPresets?.map(preset => ({ label: preset.name, value: preset.id })), [allPresets]);
	const isOptionDisabled = useCallback(option => {
		if(option.__isNew__) { return false }
		const preset = allPresets?.find(preset => preset.id === option.value);
		if(!preset) { return true }
		// disabled preset
		if(
			(preset.config.selectBy === selectedSelectBy && (!selectedSites?.length && !preset.config.siteIds?.length)) ||
			(preset.config.selectBy !== selectedSelectBy && !preset.config.siteIds?.length) ||
			(selectedCountry !== 'ALL' && preset.config.siteIds?.length && !preset.config.siteIds.every(siteId => allSites.find(site => site.site_id === siteId)?.country === selectedCountry))
		) {
			return true;
		}
		// enabled preset
		return false;
	}, [allPresets, selectedSelectBy, selectedSites, selectedCountry, allSites]);
	
	// for storing new preset name from creatable Select
	const [intermediateOption, setIntermediateOption] = useState(null);

	// dynamic variables
	const selectValue = useMemo(() => {
		if(!currentPreset) { return null }
		return { value: currentPreset.id, label: currentPreset.name };
	}, [currentPreset]);
	
	// preset rename form
	const [ renameForm ] = Form.useForm();
	const renameInputValidator = useCallback((_, value) => {
		if(!value.trim()) {
			return Promise.reject(new Error('Preset name can not be empty'));
		}
		if(allPresets.filter(preset => preset.id !== currentPreset.id).find(preset => preset.name === value.trim())) {
			return Promise.reject(new Error('You have another preset with the same name'))
		}
		return Promise.resolve()
	}, [allPresets, currentPreset]);

	// preset setting form
	const [ userRole, setUserRole ] = useState(null);
	const [ settingForm ] = Form.useForm();
	const [ presetRoleVisible, setPresetRoleVisible ] = useState(true);
	const [ presetRoleOptions, setPresetRoleOptions ] = useState([]);
	const settingFormInitialValues = useMemo(() => ({
		includeSiteIds: currentPreset?.config?.siteIds,
		includeDateRange: currentPreset?.config?.fields?.DATE_RANGE,
		isDefaultPreset: !!currentPreset?.is_default,
		presetRoles: currentPreset?.roles?.map(role => presetRoleOptions?.map(country => country.options).flat(1).find(option => option.value === role)).filter(option => option) || []
	}), [currentPreset, presetRoleOptions]);

	const resetForms = useCallback(() => {
		renameForm.resetFields();
		settingForm.resetFields();
	}, [ renameForm, settingForm ]);

	const getPresetRoleOptions = useCallback(async () => {
		if(!user) { return [] }
		if(presetRoleOptions?.length) { return presetRoleOptions }
		const _presetRoleOptions = await GYAxios.get('/data_download/preset-role-select-options').then(res => res.data);
		setPresetRoleOptions(_presetRoleOptions);
		return _presetRoleOptions;
	}, [presetRoleOptions, user]);

	const getUserPresetRole = useCallback(async () => {
		if(!user?.roles?.length) { return }
		if(!presetRoleOptions?.length) { return }
		const allRoles = presetRoleOptions.map(country => country.options.map(option => option.value.split(','))).flat(1);
		const foundUserPresetRole = allRoles.find(roles => roles.every(role => user.roles.includes(role)));
		if(!foundUserPresetRole) {
			setIsUserRolesInvalid(true);
			return Noti.create({ type: 'e', msg: "Error occured! please contact IT", title: 'Cannot determine your roles' });
		}
		const userRole = foundUserPresetRole.join(',');
		setUserRole(userRole);
		return userRole;
	}, [ presetRoleOptions, user ]);

	const handlePresetRoleVisible = useCallback(isDefaultPreset => {
		if(isDefaultPreset && user.roles.includes(enumRoles.GY_IT) && !presetRoleVisible) {
			return setPresetRoleVisible(true);
		}
		if((!isDefaultPreset || !user.roles.includes(enumRoles.GY_IT)) && presetRoleVisible) {
			return setPresetRoleVisible(false);
		}
	}, [ user, presetRoleVisible ]);

	const handlePresetSettingFormChange = (changedValue) => {
		if('isDefaultPreset' in changedValue) {
			handlePresetRoleVisible(changedValue.isDefaultPreset);
		}
	};


	useWatchStateChange((depIndex, prevValue, currentValue) => {
		if(depIndex > 1) { return } // only focus on currentPreset changes
		resetForms();
		if(currentPreset) {
			handlePresetRoleVisible(!!currentPreset.is_default);
		}
		if(!currentPreset) {
			handlePresetRoleVisible(false);
		}
	}, [ currentPreset, editingPreset, resetForms, handlePresetRoleVisible]);

	// load preset on first mount, edit / delete presets
	const loadPresets = useCallback(async (options) => {
		if(!userRole) { return }
		// fetch preset
		const automaticSelect = options?.automaticSelect;
		const forceSelect = options?.forceSelect;
		return GYAxios.get(`/data_download/presets?userRoles=${encodeURIComponent(userRole)}`)
			.then(res => res.data)
			.then(presets => {
				const lastUsedPresets  = presets.filter(preset =>  preset.last_used_at).sort((a, b) => new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime());
				const neverUsedPresets = presets.filter(preset => !preset.last_used_at).sort((a, b) => new Date(b.updated_at).getTime()   - new Date(a.updated_at).getTime());
				// set all presets
				const sortedPresets = [ ...lastUsedPresets, ...neverUsedPresets ];
				sortedPresets.forEach(preset => {
					if(preset.config?.fields?.DATE_RANGE?.range?.startDate && preset.config?.fields?.DATE_RANGE?.range?.endDate) {
						preset.config.fields.DATE_RANGE.range = { startDate: moment(preset.config.fields.DATE_RANGE.range.startDate), endDate: moment(preset.config.fields.DATE_RANGE.range.endDate) }
					}
					if(preset.config?.fields?.DATE_RANGE?.label && typeof(preset.config.fields.DATE_RANGE.label) == 'string') {
						const predefinedRange = dateRangePickerRanges[preset.config.fields.DATE_RANGE.label];
						preset.config.fields.DATE_RANGE.range = { startDate: predefinedRange[0], endDate: predefinedRange[1] };
					}
					// by this line shape of preset.config.dateRange = { range: { startDate: moment(), endDate: moment() } } | { label: String }
				});
				setAllPresets(sortedPresets);
				if(!forceSelect && (isEditPreset || isCreatePreset)) { return }
				// useful vars
				const presetsForType = presets.filter(preset => preset.config.dataType === dataType);
				const lastUsedPreset = lastUsedPresets.filter(preset => preset.config.dataType === dataType)[0];
				const latestPreset = presetsForType[presetsForType.length-1];
				// FOR FIRST TIME
				if(isFirstLoadPresets.current) {
					const preset = automaticSelect === 'LAST_USED' ? (lastUsedPreset || latestPreset) : automaticSelect === 'LATEST' ? (latestPreset || lastUsedPreset) : null;
					// update selected preset if possible
					if(preset && !currentPreset) { onPresetChange(preset) }
					isFirstLoadPresets.current = false;
					setPresetsLoaded(true);
				}
				// FOR LATER TIME
				if(!isFirstLoadPresets.current) {
					// there is preset selected
					if(currentPreset) {
						const preset = presets.find(preset => preset.id === currentPreset.id);
						// if preset is deleted, update selected preset
						if(!preset) {
							const toSelectPreset = automaticSelect === 'LAST_USED' ? (lastUsedPreset || latestPreset) : automaticSelect === 'LATEST' ? (latestPreset || lastUsedPreset) : null;
							onPresetChange(toSelectPreset || null)
						}
						// if preset changes, update selected preset
						if(preset && (
							preset.name !== currentPreset.name ||
							JSON.stringify(preset.config) !== JSON.stringify(currentPreset.config) ||
							JSON.stringify(preset.siteIds) !== JSON.stringify(currentPreset.siteIds) ||
							JSON.stringify(preset.roles) !== JSON.stringify(currentPreset.roles) ||
							preset.is_default !== currentPreset.is_default)
						) {
							onPresetChange(preset);
						}
					}
					// no preset selected
					if(!currentPreset && automaticSelect) {
						const preset = automaticSelect === 'LAST_USED' ? (lastUsedPreset || latestPreset) : automaticSelect === 'LATEST' ? (latestPreset || lastUsedPreset) : null;
						onPresetChange(preset || null);
					}
				}
				// reset form fields
				resetForms()
			});
	}, [userRole, currentPreset, isEditPreset, isCreatePreset, resetForms, dateRangePickerRanges, dataType, onPresetChange]);

	const updateLastUsed = useCallback(async presetId => GYAxios.post(`/data_download/presets/${presetId}/update-last_used_at`), []);

	const handleIntermediateCreatePreset = useCallback((intermediateOption) => {
		setIntermediateOption(intermediateOption);
		setIsCreatePreset(!!intermediateOption);
	}, [setIsCreatePreset]);

	const handleOnSelectChange = useCallback(option => {
		setEditingPreset(null);
		
		// create preset from creatable select
		if(option.__isNew__) {
			if(!option.label.trim()) { return alert('Preset name cannot be empty') }
			if(!isAllSelectSelected) { return Noti.create({ type: 'e', msg: 'You have to select required data before creating custom preset' }) }
			handleIntermediateCreatePreset(option);
			return;
		}
		// select presets from the list
		const preset = allPresets?.find(preset => preset.id === option.value);
		if(preset) {
			updateLastUsed(preset.id).then(() => loadPresets());
			onPresetChange(preset);
		}
		// clear preset
		if(!preset) {
			onPresetChange(null);
		}
	}, [setEditingPreset, allPresets, isAllSelectSelected, handleIntermediateCreatePreset, updateLastUsed, onPresetChange, loadPresets]);

	const handleOnSelectChangeWithDispatch = useCallback(async option => {
		handleOnSelectChange(option);
		if(option.__isNew__) { // for intermediate option name, skip
			if(!currentFieldSelection[dataType]) {
				duplicatePresetToFieldSelection(currentPreset);
			}
			return;
		}
		handleIntermediateCreatePreset(null);
		resetCurrentFieldSelection();
		// emit events
		const preset = allPresets?.find(preset => preset.id === option.value);
		if(!preset) { return }
		if(preset.id === currentPreset?.id) { return }
		// select "Getting data by contracts"
		if(preset.config.selectBy !== selectedSelectBy) {
			await presetEventEmitter.dispatch(presetEventNames.SET_SELECT_GET_DATA_BY, { value: preset.config.selectBy });
		}
		// select contracts if "Getting data by" === 'contract'
		if(preset.config.selectBy === 'contract') {
			await presetEventEmitter.dispatch(presetEventNames.SET_SELECT_CONTRACTS, { contractIds: preset.config.contractIds });
		}
		if(preset.config.siteIds) {
			await delay(10);
			const reloadP10Form = preset.config.dataType === 1 && preset.config.dataType === dataType;
			await presetEventEmitter.dispatch(presetEventNames.SET_SELECT_SITE, { siteIds: preset.config.siteIds || [], reloadP10Form });
		}
		// handle P10 preset conflicts
		if(!preset.config.siteIds && preset.config.dataType === 1 && selectedSites.length > 1) {
			await delay(10);
			await presetEventEmitter.dispatch(presetEventNames.SET_SELECT_SITE, { siteIds: [selectedSites[0].site_id] });
		}
		// select data type according to preset
		await delay(100);
		await presetEventEmitter.dispatch(presetEventNames.SET_SELECT_DATA_TYPE, { dataTypeId: preset.config.dataType });
	}, [allPresets, currentFieldSelection, currentPreset, dataType, duplicatePresetToFieldSelection, handleIntermediateCreatePreset, handleOnSelectChange, resetCurrentFieldSelection, selectedSelectBy, selectedSites]);


	const handleEditClick = useCallback(async presetOption => {
		const presetId = presetOption.value;
		const currentPreset = allPresets?.find(preset => preset.id === presetId);
		await handleOnSelectChangeWithDispatch(presetOption);
		duplicatePresetToFieldSelection(currentPreset);
		setEditingPreset(currentPreset);
	} , [allPresets, handleOnSelectChangeWithDispatch, duplicatePresetToFieldSelection, setEditingPreset]);

	const handleCloseEdit = useCallback(() => {
		resetForms();
		setEditingPreset(null);
		resetCurrentFieldSelection();
	}, [resetCurrentFieldSelection, setEditingPreset, resetForms]);

	const savePreset = useCallback(async ({ id=null, name, is_default, config, roles }, { isUpdateLastUsed, automaticSelect, forceSelect }={}) => {
		const payload = { id, name, is_default, config, roles };
		return GYAxios.post('/data_download/presets', payload)
			.then(res => res.data)
			.then(result => {
				Noti.create({
					type: "s",
					msg: "Save preset succesfully",
				});
				if(isUpdateLastUsed) {
					return updateLastUsed(result.insertId).then(() => loadPresets({ automaticSelect, forceSelect }));
				}
				if(!isUpdateLastUsed) {
					return loadPresets({ automaticSelect, forceSelect });
				}
			})
			.catch(err => Noti.create({
				type: "e",
				msg: "Error occured",
				title: err.response?.data?.error || null
			}));
	}, [ loadPresets, updateLastUsed ]);

	const getDateRangeToSave = useCallback((unsavendFields, presetFields) => {
		let dateRange;
		if(dataType === 1) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.timeRange} }  // P10
		if(dataType === 2) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.dateRange} }  // daily
		if(dataType === 3) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.monthRange} } // monthly
		if(dataType === 4) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.dateRange} }  // "Daily Delivered Production"
		if(dataType === 5) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.monthRange} } // "Monthly Estimated Production"
		if(dataType === 7) { dateRange = unsavendFields?.DATE_RANGE || presetFields?.DATE_RANGE || {range: defaultDateRange.yearRange} }  // "Daily Delivered Production"
		
		if(dateRange?.label) { // filter out moment range if label specified
			dateRange = { label: dateRange.label };
		} else if(dateRange?.range) { // convert moment to string
			dateRange = { range: { startDate: dateRange.range.startDate.toISOString(), endDate: dateRange.range.endDate.toISOString() } };
		} else {
			dateRange = null;
		}
		return dateRange;
	}, [ dataType, defaultDateRange ]);

	const getPresetDataToSave = useCallback(() => {
		const presetSettings = settingForm.getFieldsValue();
		const unsavedFields = currentFieldSelection?.[dataType];
		const presetFields = currentPreset?.config?.fields;
		const config = { selectBy: selectedSelectBy, dataType, fields: (unsavedFields && {...unsavedFields}) || (presetFields && {...presetFields}) || {} };

		// error checking
		// deny save if "get data by" is not selected
		if(!selectedSelectBy) {
			Noti.create({ type: 'e', msg: 'Please select "Get data by" before save' });
			return null;
		}
		// deny save if ["include siteIds" is checked] but no sites selected
		if(presetSettings.includeSiteIds && !selectedSites?.length) {
			Noti.create({ type: 'e', msg: 'Please select site(s) before save' });
			return null;
		}
		// deny save if data type is not selected
		if(typeof(dataType) !== 'number') {
			Noti.create({ type: 'e', msg: 'Please select data type before save' });
			return null;
		}
		// deny save if "get data by contract" and no contract selected
		if(selectedSelectBy === 'contract' && !selectedContracts?.length) {
			Noti.create({ type: 'e', msg: 'Please select contract(s) before save' });
			return null;
		}

		// contract Ids
		if(selectedSelectBy === 'contract') {
			config.contractIds = selectedContracts;
		}
		
		// selected sites
		if(presetSettings.includeSiteIds) {
			const siteIds = selectedSites.map(site => site.site_id);
			config.siteIds = siteIds;
		} else {
			config.siteIds = null;
		}

		// date range
		if(presetSettings.includeDateRange) {
			const dateRange = getDateRangeToSave(unsavedFields, presetFields);
			config.fields.DATE_RANGE = dateRange;
		} else {
			delete config.fields.DATE_RANGE;
		}

		// roles
		let presetRoles;
		if(presetSettings.isDefaultPreset && isITStaff) {
			presetRoles = presetSettings.presetRoles?.map(roleOption => roleOption.value) || [];
		} else {
			presetRoles = null;
		}

		// is_default
		const is_default = (presetSettings.isDefaultPreset && isITStaff) ? 1 : 0;

		// for P10: if "Select All" is selected, do not store other options in DB
		if(dataType === 1 && config.fields.ADVANCED) {
			const advanced = { ...config.fields.ADVANCED };
			for(let group in advanced) {
				const allOption = advanced[group]?.find(option => option.value === '_ALL');
				if(allOption) {
					advanced[group] = [allOption];
				}
			}
			config.fields.ADVANCED = advanced;
		}

		// remove unnecessary config properties (can be caused by [requirement updates] / [system updates])
		Object.keys(config.fields).forEach(key => {
			if(key !== 'ADVANCED' && key !== 'STANDARD' && key !== 'DATE_RANGE') {
				delete config.fields[key];
			}
		});

		return { is_default, config, presetRoles };

	}, [settingForm, currentFieldSelection, dataType, currentPreset, selectedSelectBy, selectedSites, isITStaff, selectedContracts, getDateRangeToSave]);

	const handleCreatePreset = useCallback(async () => {
		if(!intermediateOption) { return }

		// preset data
		const presetName = intermediateOption.label?.trim();
		const presetData = getPresetDataToSave();
		
		if(!presetData) { return }
		if(!presetName) { return }
		
		const { is_default, config, presetRoles } = presetData;
		
		await delay(10);
		await savePreset({ id: null, name: presetName, is_default, config, roles: presetRoles }, { automaticSelect: 'LATEST', forceSelect: true, isUpdateLastUsed: true });

		handleIntermediateCreatePreset(null);
		resetCurrentFieldSelection();
		resetForms();
	}, [intermediateOption, getPresetDataToSave, savePreset, handleIntermediateCreatePreset, resetCurrentFieldSelection, resetForms]);

	const handleCancelCreatePreset = useCallback(() => {
		handleIntermediateCreatePreset(null);
		resetCurrentFieldSelection();
	}, [ handleIntermediateCreatePreset, resetCurrentFieldSelection ]);

	const handleSaveEdit = useCallback(async () => {
		try {
			const { presetName } = await renameForm.validateFields();
			const presetData = getPresetDataToSave();

			if(!presetData) { return }
			if(!presetName) { return }

			const { is_default, config, presetRoles } = presetData;

			await delay(10);
			await savePreset({ id: currentPreset.id, name: presetName, is_default, config, roles: presetRoles }, { automaticSelect: 'LATEST', forceSelect: true });

			handleCloseEdit();
			resetCurrentFieldSelection();
			resetForms();
		} catch(err) {
		}
	}, [renameForm, getPresetDataToSave, savePreset, currentPreset, handleCloseEdit, resetCurrentFieldSelection, resetForms]);

	const handleDeletePreset = useCallback(presetId => {
		if(!window.confirm('Confirm delete this preset?')) { return }
		GYAxios.delete(`/data_download/presets/${presetId}`)
			.then(() => {
				Noti.create({
					type: "s",
					msg: "Delete preset succesfully",
				});
				loadPresets({ automaticSelect: 'LAST_USED' });
			});
	}, [ loadPresets ]);

	// first load presets
	useEffect(() => {
		// prevent parent re-render this component too fast (causing too many API calls)
		const timer = setTimeout(async () => {
			if(isUserRolesInvalid) { return }
			if(!presetRoleOptions?.length && !userRole) {
				await getPresetRoleOptions();
			}
			if(presetRoleOptions?.length && !userRole) {
				await getUserPresetRole();
			}
			if(isFirstLoadPresets.current) {
				if(presetRoleOptions?.length && userRole) {
					loadPresets({ automaticSelect: 'LAST_USED' });
				}
			}
		}, 100);
		return () => clearTimeout(timer);
	}, [ isUserRolesInvalid, userRole, presetRoleOptions, loadPresets, getPresetRoleOptions, getUserPresetRole ]);

	useEffect(() => {
		if(!userRole) { return }
		const callback = ({ from, dataType }) => {
			if(from === 'LAST_USED') {
				if(currentFieldSelection[dataType]) { return } // only auto-select preset if there is no unsaved input changes
				if(isEditPreset || isCreatePreset) { return }
				const dataTypePresets = [...allPresets].filter(preset => preset.config.dataType === dataType && (!preset.config.siteIds?.length || (preset.config.siteIds.length === selectedSites.length && preset.config.siteIds.every(siteId => selectedSites.find(site => site.site_id === siteId)))) && preset.last_used_at);
				dataTypePresets.sort((a, b) => new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime());
				const presetToSelect = dataTypePresets[0];
				if(!presetToSelect) { // if no preset to select for selected data type, set preset to null
					onPresetChange(null);
				}
				if(presetToSelect) { // if there is preset to select for selected data type, set preset accordingly
					const option = { value: presetToSelect.id, label: presetToSelect.name };
					handleOnSelectChange(option);
				}
			}
		}
		PresetEventEmitter.subscribe(presetEventNames.SET_SELECT_PRESET, callback); // emitted by data type select
		return () => PresetEventEmitter.unsubscribe(presetEventNames.SET_SELECT_PRESET, callback);
	}, [userRole, allPresets, currentFieldSelection, handleOnSelectChange, onPresetChange, isEditPreset, selectedSites, isCreatePreset]);

	// dynamic component
	const presetOptionEditAvailable = presetOption => (
		!isOptionDisabled(presetOption) &&
		// if IT staff OR others but this is their custom presets
		(isITStaff || (!isITStaff && !allPresets.find(preset => preset.id === presetOption.value)?.is_default))
	);
	const isDefaultOption = presetOption => !!allPresets.find(preset => preset.id === presetOption.value)?.is_default;
	const Option = props => <PresetSelectOption isITStaff={isITStaff} isDefault={isDefaultOption(props.data)} onDelete={handleDeletePreset} onEdit={handleEditClick} editAvailable={presetOptionEditAvailable(props.data)} {...props} />;
	// no standard and advanced inputs if dataType is 4 or 5 ("Daily Delivered Production" or "Monthly Estimated Production")
	const isNoInputs = dataType === 4 || dataType === 5;
	// no date range if dataType is 6 ("Site Information")
	const isNoDateRange = dataType === 6 || dataType === 8; // Site Information & Inverter Information

	// conditional vars
	const settingFormDisabled = !intermediateOption && !isEditPreset;

	// in case required user roles are missing, disable presets
	if(isUserRolesInvalid) { return null }

	const presetSelectCreatable = !selectedPreset && currentFieldSelection?.[dataType];
	const PresetSelect = presetSelectCreatable ? ReactSelectCreatable : ReactSelect;

	return (
		<>
			<div className={styles.presetContainer}>
				{!isEditPreset && (
					<PresetSelect
						options={options}
						value={intermediateOption || selectValue}
						placeholder={presetSelectCreatable ? "Or type to create custom Preset..." : "Select preset"}
						onChange={handleOnSelectChangeWithDispatch}
						isDisabled={!presetLoaded || isEditPreset}
						isOptionDisabled={isOptionDisabled}
						isSearchable={!!presetSelectCreatable}
						components={{ Option }}
						styles={selectStyle}
					/>
				)}
				
				{/* rename preset input */}
				{(currentPreset && isEditPreset) && (
					<Form form={renameForm} initialValues={{ presetName: currentPreset.name }} className={styles.presetNameForm}>
						<Form.Item name="presetName" rules={[{validator: renameInputValidator}]} style={{ marginBottom: '0' }}>
							<Input type="text" placeholder="New preset name" style={{ marginBottom: '0', width: '100%', minWidth: '200px' }} />
						</Form.Item>
					</Form>
				)}
				
				{/* Preset Settings */}
				<Form form={settingForm} initialValues={settingFormInitialValues} layout="inline" className={`${styles.presetSettingForm} ${settingFormDisabled ? styles.disabled : ''}`} onValuesChange={changedValues => { handlePresetSettingFormChange(changedValues)}}>
					<div className={styles.basicSettings}>
						<div className={styles.setting}>
							<Form.Item name="includeSiteIds" valuePropName="checked" noStyle><input type="checkbox" disabled={settingFormDisabled} /></Form.Item>
							Include sites in preset
						</div>
						<div className={`${styles.setting} ${isNoDateRange ? styles.hidden : ''}`}>
							<Form.Item name="includeDateRange" valuePropName="checked" noStyle><input type="checkbox" disabled={settingFormDisabled} /></Form.Item>
							Include date range in preset
						</div>
					</div>
					<div className={`${styles.setting} ${!user.roles.includes(enumRoles.GY_IT) ? styles.hidden : ''}`}>
						<Form.Item name="isDefaultPreset" valuePropName="checked" noStyle><input type="checkbox" disabled={settingFormDisabled} /></Form.Item>
						Default Preset
					</div>
					<div className={`${styles.setting} ${!presetRoleVisible ? styles.hidden : ''}`}>
						<Form.Item name="presetRoles" noStyle>
							<ReactSelect options={presetRoleOptions} styles={selectStyle} isDisabled={settingFormDisabled} isMulti components={{ MultiValueLabel: PresetRoleOptionMultiValueLabel }} closeMenuOnSelect={false} placeholder="Select who can see this default preset..." />
						</Form.Item>
					</div>
				</Form>
				
				{/* CREATE: Save & Cancel */}
				{isCreatePreset && <div className={styles.actionButtons}>
					<button className='btn btn-primary' type="button" onClick={handleCreatePreset}>Save</button>
					<button className='btn btn-default' type="button" onClick={handleCancelCreatePreset}>Cancel</button>
				</div>}

				{/* EDIT: Save & Cancel */}
				{(currentPreset && isEditPreset) && <div className={styles.actionButtons}>
					<button className="btn btn-primary" type="button" onClick={handleSaveEdit}>Save</button>
					<button className="btn btn-default" type="button" onClick={handleCloseEdit}>Cancel</button>
				</div>}
			</div>

			{(isEditPreset || isCreatePreset) && <Alert message="You can modify data selection below for this preset. After you finish the modification, you can click 'Save' or 'Cancel'." type="success" showIcon style={{ width: '100%', marginBottom: '1em' }} />}
		</>
	);
}